/* Nutzerprofile: Anzeigename, Bild und Rolle.
 *
 * Die Tabelle `profiles` ist die vorgesehene Heimat der Rollen — siehe
 * docs/rollen-konzept.md und supabase/profiles.sql. Sie ersetzt die
 * Nutzermetadaten, in denen die Rolle heute noch steht und die jeder Nutzer
 * bei sich selbst aendern kann.
 *
 * WARUM DIE TABELLE FEHLEN DARF: Das SQL-Skript muss von Hand im
 * Supabase-Dashboard eingespielt werden. Bis dahin liefert jede Abfrage hier
 * einen Fehler. Deshalb wird "Tabelle nicht vorhanden" gesondert erkannt und
 * als `null` bzw. `profilesMissing` weitergereicht — die Oberflaeche zeigt
 * dann einen Hinweis statt einer Fehlermeldung, und die App laeuft im
 * bisherigen Zustand weiter.
 *
 * Die Kontenliste selbst (auth.users) ist fuer die App nicht lesbar; dafuer
 * braeuchte es den Serverschluessel, der nie im Browser landen darf. Was die
 * Nutzerverwaltung zeigt, sind ausschliesslich die Zeilen dieser Tabelle.
 */

import { supabase } from './client.js';
import { ROLES } from '../domain/permissions.js';

/* Postgres meldet eine fehlende Tabelle als 42P01; PostgREST kennt sie dann
   auch in seinem Schema-Zwischenspeicher nicht (PGRST205/PGRST202). */
const MISSING_TABLE_CODES = new Set(['42P01', 'PGRST205', 'PGRST202']);

function isMissingTable(error) {
  if (!error) return false;
  if (MISSING_TABLE_CODES.has(error.code)) return true;
  return /relation .*profiles.* does not exist/i.test(error.message || '');
}

/**
 * Eigenes Profil laden.
 * @returns {Promise<object|null>} Zeile, oder null wenn es sie (noch) nicht gibt
 * @throws {Error} 'profilesMissing', wenn die Tabelle nicht angelegt wurde
 */
export async function loadOwnProfile(userId) {
  if (!supabase || !userId) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, full_name, email, avatar_path')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    if (isMissingTable(error)) throw new Error('profilesMissing');
    console.error('[profiles] Eigenes Profil laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return data || null;
}

/**
 * Eigenen Anzeigenamen und/oder das eigene Bild speichern.
 *
 * Die Rolle wird hier bewusst NICHT mitgeschickt. Selbst wenn doch, setzt der
 * Ausloeser in der Datenbank sie fuer Nicht-Admins auf den alten Wert zurueck.
 */
export async function saveOwnProfile(userId, { fullName, avatarPath } = {}) {
  if (!supabase) throw new Error('notConnected');
  if (!userId) throw new Error('saveFailed');

  const patch = { updated_at: new Date().toISOString() };
  if (fullName !== undefined) patch.full_name = fullName;
  if (avatarPath !== undefined) patch.avatar_path = avatarPath;

  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', userId)
    .select('id, role, full_name, email, avatar_path')
    .maybeSingle();

  if (error) {
    if (isMissingTable(error)) throw new Error('profilesMissing');
    console.error('[profiles] Speichern fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }

  /* Kein Fehler, aber auch keine Zeile zurueck: Zu diesem Konto gibt es
     keinen Eintrag in `profiles`. Das passiert, wenn das Konto vor dem
     Einspielen von supabase/profiles.sql angelegt und Abschnitt 6
     (Bestehende Konten nachtragen) nicht ausgefuehrt wurde.
     Ohne diese Pruefung meldete die Oberflaeche "Gespeichert", obwohl
     nichts geschrieben wurde. */
  if (!data) {
    console.error(
      '[profiles] Kein Profil zu diesem Konto — supabase/profiles.sql ' +
        'Abschnitt 6 ausfuehren, um bestehende Konten nachzutragen.'
    );
    throw new Error('saveFailed');
  }
  return data;
}

/**
 * Alle Profile — fuer die Nutzerverwaltung.
 *
 * Die Datenbankregel gibt Nicht-Admins nur die eigene Zeile heraus. Das ist
 * kein Fehlerfall: Die Oberflaeche zeigt die Verwaltung ohnehin nur Admins.
 */
export async function loadProfiles() {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, full_name, email, avatar_path, created_at')
    .order('role', { ascending: true })
    .order('email', { ascending: true });

  if (error) {
    if (isMissingTable(error)) throw new Error('profilesMissing');
    console.error('[profiles] Liste laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return data || [];
}

/**
 * Rolle eines anderen Nutzers setzen. Nur Admins — durchgesetzt wird das in
 * der Datenbank, nicht hier.
 */
export async function setProfileRole(userId, role) {
  if (!supabase) throw new Error('notConnected');
  if (!ROLES.includes(role)) throw new Error('saveFailed');

  const { data, error } = await supabase
    .from('profiles')
    .update({ role, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select('id, role, full_name, email, avatar_path')
    .maybeSingle();

  if (error) {
    if (isMissingTable(error)) throw new Error('profilesMissing');
    console.error('[profiles] Rolle setzen fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }

  /* Kein Fehler, aber auch keine Zeile zurueck: Die Datenbankregel hat den
     Schreibversuch verworfen. Das passiert genau dann, wenn der Aendernde
     doch kein Admin ist — die Oberflaeche hat sich also geirrt, die
     Datenbank nicht. */
  if (!data) throw new Error('notAllowed');
  return data;
}
